import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useShowToast } from '@/hooks';
import { getErrorMessage } from '@/utils/errors';
import { handleFraudDetectorError } from '@/utils/errors/Modules/fraud-detector';
import { FRAUD_DETECTOR_JOB_API } from '../api/fraudDetector.api';
import type {
  ExistingCandidatesPopupData,
  FraudDetectorFormValues,
} from '../types/fraudDetector.types';
import { isExistingCandidatesPopup } from '../types/fraudDetector.types';
import { fraudDetectorTeammateKeys } from '../hooks/useFraudDetectorTeammate';

export const useSaveFraudDetectorSettings = (
  jobId: string | number,
  mapFormValuesToPayload: (
    _jobId: string | number,
    _values: FraudDetectorFormValues,
    _feSurvey: boolean,
    _currentUserId: number | undefined
  ) => any,
  feSurvey: boolean,
  currentUserId: number | undefined,
  onSuccess?: () => void,
  onShowPopup?: (_data: ExistingCandidatesPopupData) => void
) => {
  const toast = useShowToast();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (values: FraudDetectorFormValues) =>
      FRAUD_DETECTOR_JOB_API.saveJobSettings(
        mapFormValuesToPayload(jobId, values, feSurvey, currentUserId)
      ),
    onSuccess: (data) => {
      if (isExistingCandidatesPopup(data)) {
        queryClient.invalidateQueries({
          queryKey: fraudDetectorTeammateKeys.jobSettings(jobId),
        });
        onShowPopup?.(data);
        return;
      }
      queryClient.invalidateQueries({
        queryKey: fraudDetectorTeammateKeys.jobSettings(jobId),
      });
      toast({ title: 'Fraud detector added successfully', status: 'success' });
      onSuccess?.();
    },
    onError: (error: any) => {
      const errorMessage = getErrorMessage(
        error,
        'fraudDetector',
        handleFraudDetectorError
      );
      toast({
        title: errorMessage,
        status: 'error',
      });
    },
  });
};

export const useDeleteFraudDetectorSettings = (
  jobId: string | number,
  onSuccess?: () => void
) => {
  const toast = useShowToast();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: () => {
      const existingSettings = queryClient.getQueryData<any>(
        fraudDetectorTeammateKeys.jobSettings(jobId)
      );

      return FRAUD_DETECTOR_JOB_API.saveJobSettings({
        ats_job_id: Number(jobId),
        contact_background_verification_integration_enabled: false,
        contact_background_verification_auto_verification_enabled: false,
        contact_background_verification_auto_reject_enabled: false,
        contact_background_verification_auto_reject_ats_rejection_reason_id:
          null,
        contact_background_verification_auto_reject_send_email: false,
        contact_background_verification_auto_reject_ats_form_template_id: null,
        contact_background_verification_auto_reject_survey_form_template_id:
          existingSettings?.contact_background_verification_auto_reject_survey_form_template_id ??
          null,
        contact_background_verification_auto_reject_ats_comm_template_id: null,
        contact_background_verification_auto_reject_email_subject: null,
        contact_background_verification_auto_reject_email_body: null,
        contact_background_verification_auto_reject_email_sender_id: null,
        contact_background_verification_auto_reject_ats_shared_email_id: null,
        contact_background_verification_auto_reject_cc: [],
        contact_background_verification_auto_reject_bcc: [],
        contact_background_verification_auto_reject_email_attachments: [],
        contact_background_verification_auto_reject_schedule: false,
        contact_background_verification_auto_reject_send_after: null,
        contact_background_verification_auto_reject_scheduled_at: null,
        contact_background_verification_auto_reject_scheduled_at_timezone: null,
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: fraudDetectorTeammateKeys.jobSettings(jobId),
      });
      toast({ title: 'Fraud detector removed' });
      onSuccess?.();
    },
    onError: (error: any) => {
      const errorMessage = getErrorMessage(
        error,
        'fraudDetector',
        handleFraudDetectorError
      );
      toast({ title: errorMessage, status: 'error' });
    },
  });
};
